import axios, { CancelTokenSource } from "axios";
import { ChannelArrayValue } from "@/models/campaigns/CRMChannel";
import { DataSourceResult } from "@/models/DataSourceResult";
import { SelectListItem } from "@/models/MultiselectItem";
import { PhoneWithChannel } from "@/models/phones/PhoneWithChannel";
import { Result } from "@/models/Result";
import { requestConfig } from "./ApiService";
import ConfigService from "../ConfigService";

export default {
  getPhones(payload: PhonesPayload, cancelTokenSource?: CancelTokenSource): Promise<DataSourceResult> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.phonesUrl);
      if (payload.channelNames) {
        url.searchParams.append("channelNames", payload.channelNames.join(","));
      }
      if (payload.dateFrom) {
        url.searchParams.append("dateFrom", payload.dateFrom.toString());
      }
      if (payload.dateTo) {
        url.searchParams.append("dateTo", payload.dateTo.toString());
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.group) {
        url.searchParams.append("group", payload.group);
      }
      if (payload.page) {
        url.searchParams.append("page", payload.page.toString());
      }
      if (payload.pageSize) {
        url.searchParams.append("pageSize", payload.pageSize.toString());
      }
      if (payload.sort) {
        url.searchParams.append("sort", payload.sort);
      }
      const config = {
        ...requestConfig,
        cancelToken: cancelTokenSource?.token,
      };
      axios.post(url.toString(), null, config).then((response) => {
        resolve(response.data);
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
    });
  },

  getPhonesChannels(phoneId?: number): Promise<ChannelArrayValue[]> {
    const url = new URL(ConfigService.phonesChannelsUrl);
    if (phoneId) {
      url.searchParams.append("initPhone", phoneId.toString());
    }
    return new Promise((resolve, reject) => {
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getPhonesPageData(): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.phonesPageDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getPhonesPrompts(companyId: number | undefined, isRingbackTone: boolean): Promise<SelectListItem[]> {
    const url = new URL(ConfigService.phonesPromptsUrl);
    if (companyId) {
      url.searchParams.append("preSetCompanyId", companyId.toString());
    }
    url.searchParams.append("isRingbackTone", isRingbackTone.toString());
    return new Promise((resolve, reject) => {
      axios
        .get(url.toString(), requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  releasePhone(id: number, preSetCompanyId?: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.releasePhoneUrl);
      const config = {
        ...requestConfig,
        params: {
          id,
          preSetCompanyId,
        },
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg,
            Success: response.data.success,
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updatePhone(phone: PhoneWithChannel): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.updatePhoneUrl);
      axios
        .post(url.toString(), phone, requestConfig)
        .then((response) => {
          if (response.data.success !== undefined) {
            resolve({
              Message: response.data.msg,
              Success: response.data.success
            });
          } else {
            const messageParts: string[] = [];
            if (response.data.Errors) {
              for (const [key, value] of Object.entries(response.data.Errors)) {
                if ((value as any).errors) {
                  messageParts.push(((value as any).errors as string[]).join(", "))
                }
              }
            }
            resolve({
              Message: messageParts.join(". "),
              Success: messageParts.length === 0
            });
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  uploadAudioFiles(file: File, companyId: number | undefined): Promise<Result> {
    const url = new URL(ConfigService.uploadAudioFilesUrl);
    if (companyId) {
      url.searchParams.append("preSetCompanyId", companyId.toString());
    }
    const formData = new FormData();
    formData.append('file', file);
    const config = {
      ...requestConfig,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    return new Promise((resolve, reject) => {
      axios
        .post(url.toString(), formData, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  syncWithMaskyoo(preSetCompanyId?: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.phonesSyncUrl);
      const config = {
        ...requestConfig,
        params: {
          preSetCompanyId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg || "Sync completed successfully",
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export interface PhonesPayload {
  dateFrom?: string;
  dateTo?: string;
  filter?: string;
  group?: string;
  page?: number;
  pageSize: number;
  sort?: string;
  channelNames?: string[];
}
